import React, { ReactNode } from 'react';
import { RouteResult } from './RouteResults';

interface BaseRouteCardProps {
  routeResult: RouteResult;
  selectedDate: string;
  language: string;
  t: any;
  
  // Optional customizations
  actionButton?: ReactNode; // For bookmark button or delete button
  className?: string; // Additional styling
  showDragHandle?: boolean;
  dragHandleProps?: any;
  bookmarkedAt?: Date; // For showing when the route was bookmarked
}

export const BaseRouteCard: React.FC<BaseRouteCardProps> = ({
  routeResult,
  selectedDate,
  language,
  t,
  actionButton,
  className = "",
  showDragHandle = false,
  dragHandleProps,
  bookmarkedAt
}) => {
  const formatOperatingDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    };
    return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', options);
  };

  const renderStopName = (stopName: string) => {
    const translatedName = t.getStopName(stopName);
    const hasParentheses = translatedName.includes('(') && translatedName.includes(')');

    // Special handling for "Takamatsu Higashi" in English - split into two lines
    if (stopName === 'Takamatsu Higashi' && language === 'en') {
      return (
        <>
          <div className="text-base font-ferry-place text-gray-700 font-bold">Takamatsu</div>
          <div className="text-base font-ferry-place text-gray-700 font-bold">Higashi</div>
        </>
      );
    }

    if (hasParentheses) {
      const [mainName, parenthetical] = translatedName.split('(');
      return (
        <>
          <div className={`${language === 'zh' ? 'text-2xl font-chinese-serif' : 'text-base font-ferry-place'} text-gray-700 font-bold`}>{mainName.trim()}</div>
          <div className={`${language === 'zh' ? 'text-base font-chinese-serif' : 'text-sm font-ferry-place'} text-gray-500`}>({parenthetical}</div>
        </>
      );
    } else {
      return <div className={`${language === 'zh' ? 'text-2xl font-chinese-serif' : 'text-base font-ferry-place'} text-gray-700 font-bold`}>{translatedName}</div>;
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      if (mins === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${mins}m`;
      }
    }
    return `${mins}m`;
  };

  const formatDurationChinese = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      if (mins === 0) {
        return `${hours}小时`;
      } else {
        return `${hours}小时${mins}分钟`;
      }
    }
    return `${mins}分钟`;
  };

  const getCapabilityWarnings = (service: any, schedule: any) => {
    // Don't show capability warnings for hazmat-only services
    if (schedule.special === 'hazmat_only') {
      return null;
    }

    const restrictions = [];
    if (!service.bicycles) restrictions.push('bikes');
    if (!service.vehicles) restrictions.push('cars', 'trucks');

    if (restrictions.length === 0) return null;

    // Generate warning key for translation
    if (restrictions.includes('bikes') && restrictions.includes('cars')) {
      return t.getWarningText('no_all_vehicles');
    } else if (restrictions.includes('cars')) {
      return t.getWarningText('no_cars_trucks');
    } else if (restrictions.includes('bikes')) {
      return t.getWarningText('no_bikes');
    }
    return `No ${restrictions.join(', ')}`;
  };

  const getMuseumClosureWarning = (route: any) => {
    // Check if this is the museum-dependent route
    if (route.id === 'naoshima_teshima_inujima') {
      return t.getWarningText('museum_closure');
    }
    return null;
  };

  const getHealthProtocolWarning = (route: any) => {
    // Check if this is the health protocol route (Oshima)
    if (route.id === 'takamatsu_oshima') {
      return t.getWarningText('health_protocols');
    }
    return null;
  };

  const getCounterPurchaseWarning = (route: any, schedule: any, fromStop: any) => {
    if (
      route.id === 'shin_okayama_tonosho' &&
      fromStop.id === 'shin_okayama' &&
      schedule.departure === '06:20'
    ) {
      return t.counterPurchaseOnly;
    }
    return null;
  };

  const getFestivalWarning = (schedule: any) => {
    if (schedule.special === 'summer_megijima_aug1_20') {
      return 'Only operates 08/01 ~ 08/20';
    }
    if (schedule.special === 'setouchi_triennale_2025') {
      return 'Operates only during Triennale 2025';
    }
    if (schedule.special === 'commuter_rush') {
      return t.getWarningText('commuter_rush');
    }
    if (schedule.special === 'hazmat_only') {
      return t.getWarningText('hazmat_only');
    }
    if (schedule.special === 'hazardous_materials_first_tuesday') {
      return t.getWarningText('hazardous_materials_first_tuesday');
    }
    return null;
  };

  const getSkippedStopsWarning = (service: any, route: any) => {
    if (!service.skippedStops || service.skippedStops.length === 0) {
      return null;
    }

    // Get stop names from route.stops array
    const skippedStopNames = service.skippedStops
      .map((stopId: string) => {
        // route.stops is an array of stop IDs
        return t.getStopName ? t.getStopName(stopId) : stopId;
      })
      .join(', ');

    if (language === 'zh') {
      return `此班次跳过: ${skippedStopNames}`;
    } else {
      return `This service skips: ${skippedStopNames}`;
    }
  };

  const calculateRealDepartureTime = (departureTime: string, selectedDate: string) => {
    const now = new Date();
    const departureDateTime = new Date(`${selectedDate}T${departureTime}:00`);
    const diffMs = departureDateTime.getTime() - now.getTime();
    
    if (diffMs <= 0) return language === 'zh' ? '已出发' : 'Departed';
    
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (language === 'zh') {
      if (diffDays > 0) {
        const remainingHours = diffHours % 24;
        return `${diffDays}天${remainingHours}小时`;
      } else if (diffHours > 0) {
        const remainingMinutes = diffMinutes % 60;
        if (remainingMinutes === 0) {
          return `${diffHours}小时`;
        }
        return `${diffHours}小时${remainingMinutes}分钟`;
      } else {
        return `${diffMinutes}分钟`;
      }
    } else {
      if (diffDays > 0) {
        const remainingHours = diffHours % 24;
        return `${diffDays}d ${remainingHours}h`;
      } else if (diffHours > 0) {
        const remainingMinutes = diffMinutes % 60;
        return `${diffHours}h ${remainingMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
  };

  return (
    <div className={`bg-white border-8 border-white relative color-bar rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-[180ms] ${className}`}>
      {/* Top Blue Bar with Bookmarked Date and Operator */}
      <div className="px-4 py-1 relative" style={{backgroundColor: '#E6F5FF'}}>
        <div className="flex justify-between items-center">
          <div className={`text-gray-600 text-xs ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
            {bookmarkedAt && formatOperatingDate(selectedDate)}
          </div>
          {routeResult.route.operatorWebsite ? (
            <a
              href={routeResult.route.operatorWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-gray-600 hover:text-blue-600 text-xs underline hover:no-underline transition-colors ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {t.getOperator(routeResult.route.operator)}
            </a>
          ) : (
            <div className={`text-gray-600 text-xs ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
              {t.getOperator(routeResult.route.operator)}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Drag Handle */}
        {showDragHandle && dragHandleProps && (
          <div 
            className="absolute left-1/2 transform -translate-x-1/2 -top-3.5 cursor-grab active:cursor-grabbing p-2 group"
            {...dragHandleProps}
            style={{ width: '80px', height: '16px' }}
          >
            <div className="bg-gray-200 group-hover:bg-gray-600 group-active:bg-gray-700 rounded-full transition-colors mx-auto" style={{ width: '40px', height: '4px' }}>
            </div>
          </div>
        )}

        {/* Main Timeline Content */}
      <div className="flex items-start">
        {/* Departure */}
        <div className="text-left flex-shrink-0">
          <div className="text-2xl font-bold text-gray-900 font-ferry-time">  
            {routeResult.schedule.departure}
          </div>
          <div className="font-ferry-place">
            {renderStopName(routeResult.fromStop.name)}
          </div>
        </div>

        {/* Timeline Center */}
        <div className="flex-1 mx-2 flex flex-col items-center">
          {/* Duration */}
          <div className="text-center">
            <div className={`text-sm text-gray-500 font-medium ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
              {language === 'zh' ? formatDurationChinese(routeResult.service.duration) : formatDuration(routeResult.service.duration)}
            </div>
          </div>
          
          {/* Timeline Line */}
          <div className="flex items-center w-full my-1">
            <div className="w-1.5 h-1.5 bg-gray-900 rounded-full"></div>
            <div className="h-px bg-gray-900 flex-1 relative">
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                <div className="w-0 h-0 border-l-[6px] border-l-gray-900 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent"></div>
              </div>
            </div>
          </div>
          
          {/* Vehicle Type */}
          <div className="text-center">
            <div className={`text-xs text-gray-500 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
              {t.getServiceType(routeResult.service.name)}
            </div>
          </div>
        </div>

        {/* Arrival */}
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-bold text-gray-900 font-ferry-time relative inline-block">
            {routeResult.schedule.arrival}
            {routeResult.schedule.special === 'next_day_arrival' && (
              <span className="absolute -top-1 -right-4 text-xs font-semibold text-gray-900">
                +1
              </span>
            )}
          </div>
          <div className="font-ferry-place">
            {renderStopName(routeResult.toStop.name)}
          </div>
        </div>
      </div>

      {/* Bottom Info Row */}
      <div className="mt-3 py-3 px-4 -mx-4 -mb-4 relative" style={{backgroundColor: '#E6F5FF'}}>
        {/* Action Button (Bookmark or Delete) */}
        {actionButton && (
          <div className="absolute top-1/2 transform -translate-y-1/2 right-6 z-10">
            {actionButton}
          </div>
        )}
        
        {(() => {
          const now = new Date();
          const departureDateTime = new Date(`${selectedDate}T${routeResult.schedule.departure}:00`);
          const diffMs = departureDateTime.getTime() - now.getTime();
          const diffMinutes = Math.floor(diffMs / 60000);
          
          const departureText = calculateRealDepartureTime(routeResult.schedule.departure, selectedDate);
          const isDeparted = departureText === 'Departed' || departureText === '已出发';
          
          // Only show remaining time info if departed or within 60 minutes
          if (isDeparted || diffMinutes <= 60) {
            return (
              <div className={`text-gray-600 text-lg font-condensed ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                {isDeparted ? (
                  <span className="font-semibold text-gray-900">
                    {departureText}
                  </span>
                ) : (
                  <>
                    <span className="font-semibold text-gray-900 ">
                      {departureText}
                    </span>
                    <span className="text-sm"> {t.departsIn}</span>
                  </>
                )}
              </div>
            );
          }
          // Don't show anything if more than 60 minutes away
          return null;
        })()}
        
        {(() => {
          const warnings = [
            getFestivalWarning(routeResult.schedule),
            getSkippedStopsWarning(routeResult.service, routeResult.route),
            getCapabilityWarnings(routeResult.service, routeResult.schedule),
            getMuseumClosureWarning(routeResult.route),
            getHealthProtocolWarning(routeResult.route),
            getCounterPurchaseWarning(routeResult.route, routeResult.schedule, routeResult.fromStop)
          ].filter(Boolean);
          
          const isSingleWarning = warnings.length === 1;
          
          return (
            <div className={`${isSingleWarning ? 'flex items-center justify-start' : 'space-y-1'}`}>
              {getFestivalWarning(routeResult.schedule) && (
                <div className={`flex items-center text-blue-800 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-blue-800 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getFestivalWarning(routeResult.schedule)}
                  </span>
                </div>
              )}

              {getSkippedStopsWarning(routeResult.service, routeResult.route) && (
                <div className={`flex items-center text-blue-800 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-blue-800 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getSkippedStopsWarning(routeResult.service, routeResult.route)}
                  </span>
                </div>
              )}

              {getCapabilityWarnings(routeResult.service, routeResult.schedule) && (
                <div className={`flex items-center text-blue-800 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-blue-800 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getCapabilityWarnings(routeResult.service, routeResult.schedule)}
                  </span>
                </div>
              )}
              
              {getMuseumClosureWarning(routeResult.route) && (
                <div className={`flex items-center text-amber-700 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-amber-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-amber-700 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getMuseumClosureWarning(routeResult.route)}
                  </span>
                </div>
              )}
              
             {getHealthProtocolWarning(routeResult.route) && (
                <div className={`flex items-center text-blue-800 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-blue-800 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getHealthProtocolWarning(routeResult.route)}
                  </span>
                </div>
              )}

              {getCounterPurchaseWarning(routeResult.route, routeResult.schedule, routeResult.fromStop) && (
                <div className={`flex items-center text-blue-800 ${!isSingleWarning ? 'mt-2' : ''}`}>
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-blue-800 text-xs font-bold">!</span>
                  </div>
                  <span className={`text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    {getCounterPurchaseWarning(routeResult.route, routeResult.schedule, routeResult.fromStop)}
                  </span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      </div>
    </div>
  );
};
